// Browser keys to Windows virtual-key codes for the remote desktop, after MeshCentral's agent-desktop (Apache-2.0).
// Printable characters are sent as the characters themselves (see session.ts), so this table covers the rest.

const NAMED: Record<string, number> = {
  Backspace: 8, Tab: 9, Enter: 13, NumpadEnter: 13, ShiftLeft: 16, ShiftRight: 161, ControlLeft: 17, ControlRight: 17, AltLeft: 18, AltRight: 18,
  Pause: 19, CapsLock: 20, Escape: 27, Space: 32, PageUp: 33, PageDown: 34, End: 35, Home: 36, ArrowLeft: 37, ArrowUp: 38, ArrowRight: 39,
  ArrowDown: 40, PrintScreen: 44, Insert: 45, Delete: 46, MetaLeft: 91, MetaRight: 92, OSLeft: 91, OSRight: 92, ContextMenu: 93,
  NumpadMultiply: 106, NumpadAdd: 107, NumpadSubtract: 109, NumpadDecimal: 110, NumpadDivide: 111, NumLock: 144, ScrollLock: 145,
  Semicolon: 186, Equal: 187, Comma: 188, Minus: 189, Period: 190, Slash: 191, Backquote: 192, BracketLeft: 219, Backslash: 220,
  BracketRight: 221, Quote: 222, VolumeMute: 173, VolumeDown: 174, VolumeUp: 175, MediaTrackNext: 176, MediaTrackPrevious: 177,
  MediaPlayPause: 179,
};
/** Keys Windows marks as extended: the right-hand Ctrl and Alt, the navigation block, arrows, numpad Enter and divide. */
const EXTENDED = new Set(['AltRight', 'ControlRight', 'Home', 'End', 'Insert', 'Delete', 'PageUp', 'PageDown', 'ArrowLeft', 'ArrowUp',
  'ArrowRight', 'ArrowDown', 'NumpadDivide', 'NumpadEnter', 'NumLock', 'MetaLeft', 'MetaRight', 'ContextMenu', 'PrintScreen']);

export interface VirtualKey { code: number; extended: boolean }

/** The virtual key for a physical key, or null for one with no Windows equivalent. */
export function virtualKey(event: Pick<KeyboardEvent, 'code' | 'keyCode'>): VirtualKey | null {
  const { code } = event;
  let vk: number | undefined;
  if (/^Key[A-Z]$/.test(code)) vk = code.charCodeAt(3);
  else if (/^Digit[0-9]$/.test(code)) vk = code.charCodeAt(5);
  else if (/^Numpad[0-9]$/.test(code)) vk = 96 + Number(code[6]);
  else if (/^F([1-9]|1[0-9]|2[0-4])$/.test(code)) vk = 111 + Number(code.slice(1));
  else vk = NAMED[code];
  if (vk === undefined) vk = event.keyCode > 0 && event.keyCode < 255 ? event.keyCode : undefined;
  return vk === undefined ? null : { code: vk, extended: EXTENDED.has(code) };
}

const MODIFIERS = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'OSLeft',
  'OSRight', 'CapsLock', 'Fn']);
export const isModifier = (code: string) => MODIFIERS.has(code);
export const isCommand = (code: string) => /^(Meta|OS)(Left|Right)$/.test(code);

/** Whether the viewer runs on a Mac (or an iPad), where ⌘ does what Ctrl does on Windows and Linux. */
export function onMac(nav: Pick<Navigator, 'platform' | 'userAgent'> & { userAgentData?: { platform?: string } } = navigator) {
  return /mac|iphone|ipad|ipod/i.test(nav.userAgentData?.platform || nav.platform || nav.userAgent || '');
}

/** Whether a key press types a character (sent as that character, whatever the device's keyboard layout). */
export const typesCharacter = (event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'altKey' | 'metaKey'>) =>
  [...event.key].length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey;

/** Combinations a browser can't pass on itself, offered from the toolbar. Each step is [virtual key, extended, down]. */
export const SPECIAL_KEYS: Array<{ id: string; label: string; steps: Array<[number, boolean, boolean]> }> = [
  { id: 'start', label: 'Windows key (Start)', steps: [[91, true, true], [91, true, false]] },
  { id: 'alt-tab', label: 'Alt+Tab', steps: [[18, false, true], [9, false, true], [9, false, false], [18, false, false]] },
  { id: 'task-manager', label: 'Task Manager (Ctrl+Shift+Esc)', steps: [[17, false, true], [16, false, true], [27, false, true], [27, false, false], [16, false, false], [17, false, false]] },
  { id: 'run', label: 'Run dialog (Windows+R)', steps: [[91, true, true], [82, false, true], [82, false, false], [91, true, false]] },
  { id: 'lock', label: 'Lock the computer (Windows+L)', steps: [[91, true, true], [76, false, true], [76, false, false], [91, true, false]] },
  { id: 'print-screen', label: 'Print Screen', steps: [[44, true, true], [44, true, false]] },
];

/** Non-Windows sessions get raw keys without assuming a desktop environment's application shortcuts. */
export function specialKeys(windows: boolean) {
  if (windows) return SPECIAL_KEYS;
  return SPECIAL_KEYS.filter(k => ['start', 'alt-tab', 'print-screen'].includes(k.id))
    .map(k => k.id === 'start' ? { ...k, label: 'Super key' } : k);
}
